import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Leaf,
    Heart,
    Users,
    Target,
    TreePalm,
    Shield,
    Handshake,
    ArrowLeft
} from '@phosphor-icons/react';

export default function AboutPage() {
    const values = [
        {
            icon: <TreePalm weight="duotone" size={40} />,
            title: "Environmental Action",
            description: "We believe in hands-on learning and real-world environmental impact."
        },
        {
            icon: <Users weight="duotone" size={40} />,
            title: "Community",
            description: "Building a nationwide community of young environmental champions."
        },
        {
            icon: <Shield weight="duotone" size={40} />,
            title: "Safety First",
            description: "Parent-controlled accounts and age-appropriate content for kids."
        },
        {
            icon: <Heart weight="duotone" size={40} />,
            title: "Fun Learning",
            description: "Making environmental education engaging through gamification."
        }
    ];

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16 md:h-20">
                        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#22c55e] border-4 border-[#1e293b] flex items-center justify-center">
                                <Leaf weight="fill" className="text-white" size={24} />
                            </div>
                            <span className="font-fredoka text-xl md:text-2xl font-bold text-[#1e293b]">
                                Bush Rangers
                            </span>
                        </Link>
                        
                        <Link 
                            to="/"
                            className="flex items-center gap-2 text-[#475569] hover:text-[#22c55e] font-semibold"
                            data-testid="back-to-home"
                        >
                            <ArrowLeft weight="bold" size={20} />
                            <span className="hidden sm:inline">Back to Home</span>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-24 md:pt-32 pb-16 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <h1 className="font-fredoka text-4xl md:text-5xl lg:text-6xl font-bold text-[#1e293b] mb-6">
                            About <span className="text-[#22c55e]">Bush Rangers</span>
                        </h1>
                        <p className="text-lg md:text-xl text-[#475569] leading-relaxed max-w-3xl mx-auto">
                            Bush Rangers is Australia's leading digital platform turning children into active environmental participants through missions, rewards, and engaging educational content.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Mission Section */}
            <section className="py-16 px-4 bg-white border-y-4 border-[#1e293b]">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="rounded-2xl border-4 border-[#1e293b] shadow-[4px_4px_0px_0px_#1e293b] p-8 md:p-12 bg-gradient-to-br from-[#22c55e] to-[#16a34a] text-white"
                    >
                        <h2 className="font-fredoka text-3xl md:text-4xl font-bold mb-6">
                            Our Mission
                        </h2>
                        <p className="text-lg md:text-xl leading-relaxed text-white/90">
                            To inspire and empower young adventurers of all ages to become environmental stewards by making conservation fun, educational, and impactful. We believe that today's young rangers are tomorrow's environmental leaders.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Values Section */}
            <section className="py-16 md:py-24 px-4">
                <div className="max-w-7xl mx-auto">
                    <motion.h2 
                        className="font-fredoka text-3xl md:text-4xl font-bold text-[#1e293b] text-center mb-12"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        Our Values
                    </motion.h2>
                    
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {values.map((value, index) => (
                            <motion.div
                                key={value.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="brutalist-card p-6 text-center"
                            >
                                <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-[#22c55e]/10 border-4 border-[#1e293b] flex items-center justify-center text-[#22c55e]">
                                    {value.icon}
                                </div>
                                <h3 className="font-fredoka font-bold text-xl text-[#1e293b] mb-2">
                                    {value.title}
                                </h3>
                                <p className="text-[#475569]">
                                    {value.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-16 px-4 bg-[#38bdf8]">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="font-fredoka text-3xl md:text-4xl font-bold text-white mb-8">
                            How It Works
                        </h2>
                        
                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="brutalist-card p-6 bg-white">
                                <div className="w-12 h-12 rounded-full bg-[#22c55e] text-white flex items-center justify-center font-fredoka font-bold text-xl mb-4 mx-auto border-4 border-[#1e293b]">
                                    1
                                </div>
                                <h3 className="font-fredoka font-bold text-lg text-[#1e293b] mb-2">
                                    Parents Sign Up
                                </h3>
                                <p className="text-sm text-[#475569]">
                                    Create a parent account and add child profiles safely and securely.
                                </p>
                            </div>
                            <div className="brutalist-card p-6 bg-white">
                                <div className="w-12 h-12 rounded-full bg-[#fde047] text-[#1e293b] flex items-center justify-center font-fredoka font-bold text-xl mb-4 mx-auto border-4 border-[#1e293b]">
                                    2
                                </div>
                                <h3 className="font-fredoka font-bold text-lg text-[#1e293b] mb-2">
                                    Complete Missions
                                </h3>
                                <p className="text-sm text-[#475569]">
                                    Kids complete real-world environmental activities and earn Bush Tokens.
                                </p>
                            </div>
                            <div className="brutalist-card p-6 bg-white">
                                <div className="w-12 h-12 rounded-full bg-[#fb7185] text-white flex items-center justify-center font-fredoka font-bold text-xl mb-4 mx-auto border-4 border-[#1e293b]">
                                    3
                                </div>
                                <h3 className="font-fredoka font-bold text-lg text-[#1e293b] mb-2">
                                    Level Up & Earn
                                </h3>
                                <p className="text-sm text-[#475569]">
                                    Collect badges, climb the leaderboard, and make real impact!
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Partners Section */}
            <section className="py-16 md:py-24 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <Handshake weight="duotone" className="text-[#22c55e] mx-auto mb-4" size={64} />
                        <h2 className="font-fredoka text-3xl md:text-4xl font-bold text-[#1e293b] mb-6">
                            Partner With Us
                        </h2>
                        <p className="text-lg text-[#475569] mb-8 max-w-2xl mx-auto">
                            We're always looking to partner with schools, councils, and environmental organisations to expand our impact. Together, we can inspire the next generation of environmental champions.
                        </p>
                        <a href="mailto:partners@rangerquest.com.au">
                            <motion.button
                                whileHover={{ y: -2, x: -2 }}
                                whileTap={{ y: 2, x: 2 }}
                                className="brutalist-btn bg-[#22c55e] text-white text-lg"
                            >
                                Get In Touch
                            </motion.button>
                        </a>
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-[#1e293b] text-white py-12 px-4">
                <div className="max-w-7xl mx-auto text-center">
                    <div className="flex items-center justify-center gap-2 mb-4">
                        <div className="w-10 h-10 rounded-full bg-[#22c55e] flex items-center justify-center">
                            <Leaf weight="fill" className="text-white" size={20} />
                        </div>
                        <span className="font-fredoka text-xl font-bold">Bush Rangers</span>
                    </div>
                    <p className="text-gray-400 mb-6">
                        Empowering Australian kids to protect our unique environment.
                    </p>
                    <p className="text-gray-500 text-sm">
                        © 2026 Bush Rangers Australia. Made with <Heart weight="fill" className="inline text-[#fb7185]" size={14} /> for our planet.
                    </p>
                </div>
            </footer>
        </div>
    );
}
